// DocumentList.tsx
import React, { useState } from 'react';

interface Document {
  id: number;
  titlu: string;
  descriere: string;
  categorie: string;
  dataPublicare: string;
  utilizator: string;
  numDescarcari: number;
  fisier: string;
}

const DocumentList: React.FC = () => {
  // State pentru căutare și filtrare
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('toate');
  
  // Date exemplu
  const [documents, setDocuments] = useState<Document[]>([
    {
      id: 1,
      titlu: "Ghid pentru obținerea fondurilor europene 2025",
      descriere: "Document complet cu toate informațiile necesare pentru accesarea fondurilor europene disponibile în anul 2025.",
      categorie: "fonduri",
      dataPublicare: "10 Mar 2025",
      utilizator: "Maria Popescu",
      numDescarcari: 45,
      fisier: "ghid_fonduri_2025.pdf"
    },
    {
      id: 2,
      titlu: "Model plan de afaceri pentru sectorul IT",
      descriere: "Template detaliat pentru realizarea unui plan de afaceri în domeniul IT, cu exemple și recomandări.",
      categorie: "modele",
      dataPublicare: "25 Feb 2025",
      utilizator: "Ion Marinescu",
      numDescarcari: 32,
      fisier: "model_plan_afaceri_it.docx"
    },
    {
      id: 3,
      titlu: "Legislație fiscală 2025 - modificări importante",
      descriere: "Sinteza modificărilor legislative în domeniul fiscal cu impact asupra antreprenorilor.",
      categorie: "legislatie",
      dataPublicare: "15 Mar 2025",
      utilizator: "Elena Vasilescu",
      numDescarcari: 67,
      fisier: "modificari_fiscale_2025.pdf"
    },
    {
      id: 4,
      titlu: "Raport trimestrial sector HoReCa Vrancea",
      descriere: "Analiza pieței HoReCa din județul Vrancea pentru primul trimestru al anului 2025.",
      categorie: "rapoarte",
      dataPublicare: "03 Mar 2025",
      utilizator: "Adrian Munteanu",
      numDescarcari: 21,
      fisier: "raport_horeca_t1_2025.pdf"
    },
    {
      id: 5,
      titlu: "Formular cerere de finanțare",
      descriere: "Document standard pentru completarea unei cereri de finanțare pentru programele de dezvoltare regională.",
      categorie: "formulare",
      dataPublicare: "18 Feb 2025",
      utilizator: "Cristina Dumitru",
      numDescarcari: 53,
      fisier: "formular_cerere_finantare.docx"
    },
  ]);

  // Categorii disponibile
  const categorii = [
    { id: 'toate', nume: 'Toate categoriile' },
    { id: 'fonduri', nume: 'Fonduri și finanțări' },
    { id: 'modele', nume: 'Modele și template-uri' },
    { id: 'legislatie', nume: 'Legislație' },
    { id: 'rapoarte', nume: 'Rapoarte și analize' },
    { id: 'formulare', nume: 'Formulare' },
  ];

  // Filtrarea documentelor
  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch = doc.titlu.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         doc.descriere.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'toate' || doc.categorie === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Simularea descărcării documentului
  const handleDownload = (doc: Document) => {
    alert(`Simulare descărcare: ${doc.fisier}`);
  };

  // Navigare către încărcarea unui document nou
  const navigateToUpload = () => {
    // Aici ar veni navigația reală, pentru demo folosim un alert
    alert('Navigare către pagina de încărcare document nou');
    // În implementarea reală ar fi ceva de genul: navigate('/documente/upload')
  };

  // Navigare către documentele mele
  const navigateToMyDocuments = () => {
    alert('Navigare către Documentele mele');
    // În implementarea reală ar fi ceva de genul: navigate('/documente/mele')
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="p-4 border-b">
        <h2 className="text-xl font-semibold">Biblioteca de documente</h2>
        <p className="text-gray-600">Resurse, ghiduri și informații pentru comunitatea antreprenorială</p>
      </div>

      {/* Bara de căutare și filtrare */}
      <div className="p-4 border-b">
        <div className="flex flex-col md:flex-row justify-between gap-4">
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="Caută documente..."
                className="w-full p-2 pl-8 border rounded-md"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="absolute left-3 top-3 text-gray-400">
                🔍
              </div>
            </div>
          </div>
          <div className="flex-1 md:flex-initial">
            <select
              className="w-full p-2 border rounded-md bg-white"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {categorii.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.nume}</option>
              ))}
            </select>
          </div>
          <div className="flex space-x-2">
            <button 
              className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-md font-medium flex items-center"
              onClick={navigateToMyDocuments}
            >
              <span className="mr-2">📁</span>
              Documentele mele
            </button>
            <button 
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium flex items-center"
              onClick={navigateToUpload}
            >
              <span className="mr-2">📤</span>
              Încarcă document
            </button>
          </div>
        </div>
      </div>

      {/* Lista de documente */}
      <div className="p-4">
        {filteredDocuments.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <div className="text-5xl mb-4">📄</div>
            <p>Nu a fost găsit niciun document care să corespundă criteriilor tale de căutare.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredDocuments.map((doc) => (
              <div key={doc.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex flex-col md:flex-row justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-blue-600 mb-2">
                      {doc.titlu}
                    </h3>
                    <p className="text-gray-700 mb-3">{doc.descriere}</p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="px-2 py-1 bg-gray-100 text-xs rounded-md">
                        {categorii.find(cat => cat.id === doc.categorie)?.nume || doc.categorie}
                      </span>
                      <span className="px-2 py-1 bg-gray-100 text-xs rounded-md">
                        {doc.numDescarcari} descărcări
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <div className="flex items-center">
                        <span className="text-sm mr-2">👤</span>
                        <span>{doc.utilizator}</span>
                      </div>
                      <div className="ml-4 flex items-center">
                        <span className="text-sm mr-1">📅</span>
                        <span>{doc.dataPublicare}</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 md:mt-0 md:ml-6 flex items-center">
                    <button 
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium flex items-center"
                      onClick={() => handleDownload(doc)}
                    >
                      <span className="mr-2">⬇️</span>
                      Descarcă
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentList;